import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, Share2, Plus, X, HeartPulse, Calendar, Edit3, Trash2, LogIn, MapPin, Phone, Info, PhoneCall } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal"; 

export default function Donors() {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [search, setSearch] = useState("");
  const [bloodFilter, setBloodFilter] = useState("");
  const [visiblePhones, setVisiblePhones] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", place: "", blood: "", phone: "", lastDonated: null,
  });

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false, action: "", message: "", docId: null
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoaded(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async () => {
    const res = await getDocs(collection(db, "donors"));
    setDonors(res.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isAvailable = (date) => {
    if (!date) return true;
    const last = new Date(date);
    const today = new Date();
    return (today - last) / (1000 * 60 * 60 * 24) >= 90;
  };

  const myProfile = currentUser ? donors.find(d => d.userId === currentUser.uid) : null;

  const filtered = donors.filter((d) => {
    const place = d.place || ""; 
    return (
      (!bloodFilter || d.blood === bloodFilter) &&
      (d.name.toLowerCase().includes(search.toLowerCase()) || place.toLowerCase().includes(search.toLowerCase()))
    );
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    if (currentUser && a.userId === currentUser.uid) return -1;
    if (currentUser && b.userId === currentUser.uid) return 1;
    return 0;
  });

  const togglePhone = (id) => {
    setVisiblePhones((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleShareAll = async () => {
    const shareText = "INFO PAYANGADI: അടിയന്തര സാഹചര്യങ്ങളിൽ രക്തം കണ്ടെത്താൻ ഈ ലിങ്ക് സന്ദർശിക്കുക.";
    const shareUrl = `https://info.payangadilive.com/donors?v=${new Date().getTime()}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: "Blood Donors in Kalliasseri", text: shareText, url: shareUrl });
      } catch (error) {
        console.log("ഷെയർ ചെയ്യാൻ കഴിഞ്ഞില്ല:", error);
      }
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success("Link copied!");
    }
  };

  const handleOpenRegister = () => {
    if (!authLoaded) return; 
    if (!currentUser) {
      toast.error("രജിസ്റ്റർ ചെയ്യാൻ ദയവായി ലോഗിൻ ചെയ്യുക!");
      navigate("/auth");
      return;
    }
    if (myProfile) {
      setForm({
        name: myProfile.name, place: myProfile.place, blood: myProfile.blood, phone: myProfile.phone,
        lastDonated: myProfile.lastDonated ? new Date(myProfile.lastDonated) : null,
      });
    } else {
      setForm({ name: "", place: "", blood: "", phone: "", lastDonated: null });
    }
    setIsModalOpen(true);
  };

  const handlePreSubmit = () => {
    if (!currentUser) {
      toast.error("നിങ്ങൾ ലോഗിൻ ചെയ്തിട്ടില്ല! ദയവായി ലോഗിൻ ചെയ്യുക.");
      setIsModalOpen(false);
      navigate("/auth");
      return;
    }
    if (!form.name.trim()) return toast.error("ദയവായി നിങ്ങളുടെ പേര് നൽകുക.");
    if (!form.place.trim()) return toast.error("ദയവായി സ്ഥലം നൽകുക.");
    if (!form.blood) return toast.error("ദയവായി ബ്ലഡ് ഗ്രൂപ്പ് തിരഞ്ഞെടുക്കുക.");
    if (form.phone.length !== 10) return toast.error("ശരിയായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക.");

    setConfirmConfig({
      isOpen: true, action: "save",
      message: myProfile ? "നിങ്ങളുടെ വിവരങ്ങൾ അപ്ഡേറ്റ് ചെയ്യണോ?" : "രക്തദാതാവായി രജിസ്റ്റർ ചെയ്യണോ?"
    });
  };

  const handleDeleteClick = (id) => {
    if (!currentUser) return; 
    setConfirmConfig({
      isOpen: true, action: "delete",
      message: "നിങ്ങളുടെ പ്രൊഫൈൽ പൂർണ്ണമായും നീക്കം ചെയ്യണോ? ഈ പ്രവൃത്തി തിരിച്ചെടുക്കാനാകില്ല.", docId: id
    });
  };

  const executeConfirmAction = async () => {
    setConfirmConfig({ ...confirmConfig, isOpen: false }); 
    if (!currentUser) {
      toast.error("Unauthorized action! Please log in.");
      navigate("/auth");
      return;
    }
    setLoading(true);
    try {
      if (confirmConfig.action === "delete") {
        await deleteDoc(doc(db, "donors", confirmConfig.docId));
        toast.success("പ്രൊഫൈൽ വിജയകരമായി നീക്കം ചെയ്തു 🗑️");
        setIsModalOpen(false);
      } 
      else if (confirmConfig.action === "save") {
        let finalDate = form.lastDonated;
        if (!finalDate) {
          const pastDate = new Date();
          pastDate.setDate(pastDate.getDate() - 90); 
          finalDate = pastDate;
        }
        const docIdToSave = myProfile ? myProfile.id : currentUser.uid;
        await setDoc(doc(db, "donors", docIdToSave), {
          ...form, lastDonated: finalDate.toISOString(), userId: currentUser.uid,
        });
        toast.success(myProfile ? "വിവരങ്ങൾ അപ്ഡേറ്റ് ചെയ്തു! 🎉" : "രക്തദാതാക്കളുടെ ലിസ്റ്റിൽ ചേർത്തു! 🎉");
        setIsModalOpen(false);
      }
      fetchData(); 
    } catch (error) {
      toast.error("എന്തോ പ്രശ്നം സംഭവിച്ചു. വീണ്ടും ശ്രമിക്കുക!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Blood Donors in Kalliasseri | Info Payangadi</title>
        <meta name="description" content="കല്യാശ്ശേരി നിയോജക മണ്ഡലത്തിലെ രക്തദാതാക്കളുടെ വിവരങ്ങൾ. അടിയന്തര സാഹചര്യങ്ങളിൽ രക്തം ലഭ്യമാക്കാൻ ഈ ഡയറക്ടറി ഉപയോഗിക്കുക." />
        <meta property="og:url" content="https://info.payangadilive.com/donors" />
        <meta property="og:title" content="Blood Donors in Kalliasseri | Info Payangadi" />
        <meta property="og:image" content="https://info.payangadilive.com/blooddonors.jpg" />
      </Helmet>

      <style>{`
        .react-datepicker { font-family: inherit !important; border: none !important; border-radius: 1rem !important; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1) !important; overflow: hidden; font-size: 0.9rem !important; }
        .react-datepicker__header { background-color: white !important; border-bottom: 1px solid #f3f4f6 !important; padding-top: 1rem !important; }
        .react-datepicker__current-month { font-weight: 700 !important; color: #1f2937 !important; }
        .react-datepicker__day--selected { background-color: #dc2626 !important; color: white !important; }
      `}</style>

      <div className="min-h-[100dvh] bg-[#F8FAFC] pb-24 selection:bg-red-200 overflow-x-hidden relative">
        <div className="absolute top-[-10%] left-[-10%] w-[350px] h-[350px] bg-red-400/15 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-[20%] right-[-10%] w-[350px] h-[350px] bg-rose-400/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 md:pt-12 relative z-10">
          
          <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-5">
            <div className="flex items-center gap-3">
              <div className="p-3.5 bg-white text-red-600 rounded-2xl shadow-[0_8px_30px_rgb(220,38,38,0.12)] border border-red-50">
                <HeartPulse size={32} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight">
                  Blood <span className="text-red-600">Donors</span>
                </h2>
                <p className="text-sm sm:text-base font-medium text-slate-500 mt-1">ജീവന്റെ പാതയിലെ കാവൽക്കാർ</p>
              </div>
            </div>
            
            <div className="flex gap-3 w-full md:w-auto">
              <motion.button whileTap={{ scale: 0.95 }} onClick={handleShareAll} className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-5 py-3 rounded-xl shadow-sm transition-all font-bold text-sm">
                <Share2 size={16} strokeWidth={2.5} /> <span>Share</span>
              </motion.button>
              <motion.button whileTap={{ scale: 0.95 }} onClick={handleOpenRegister} className="flex-[2] md:flex-none flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-xl shadow-[0_8px_20px_rgba(220,38,38,0.25)] transition-all font-bold text-sm">
                {currentUser ? (myProfile ? <><Edit3 size={16} strokeWidth={2.5} /> Edit Profile</> : <><Plus size={16} strokeWidth={2.5} /> Become a Donor</>) : <><LogIn size={16} strokeWidth={2.5} /> Login to Register</>}
              </motion.button>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-4 flex flex-col sm:flex-row gap-3 sm:gap-4 px-1">
            <div className="relative flex-1 group">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-500 transition-colors" strokeWidth={2.5} />
              <input placeholder="പേരോ സ്ഥലമോ തിരയുക..." className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.03)] focus:ring-4 focus:ring-red-500/10 focus:border-red-400 outline-none transition-all text-sm sm:text-base font-semibold text-slate-800 placeholder:text-slate-400" onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="relative w-full sm:w-48 group">
              <select className="w-full appearance-none py-3.5 pl-4 pr-10 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.03)] focus:ring-4 focus:ring-red-500/10 focus:border-red-400 outline-none transition-all text-sm font-bold text-slate-700 cursor-pointer" onChange={(e) => setBloodFilter(e.target.value)}>
                <option value="">All Blood</option>
                <option>A+</option><option>A-</option><option>B+</option><option>B-</option><option>O+</option><option>O-</option><option>AB+</option><option>AB-</option>
              </select>
              <ChevronDown size={18} strokeWidth={2.5} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-500 pointer-events-none transition-colors" />
            </div>
          </div>

          {/* 🔥 INFO TEXT 🔥 */}
          <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-slate-400 mb-8 uppercase tracking-wider">
            <Info size={14} className="text-blue-400" />
            <span>രക്തം നൽകിയ ശേഷം 90 ദിവസത്തെ ഇടവേള ആവശ്യമാണ്</span>
          </div>

          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
            <AnimatePresence>
              {sortedFiltered.map((d) => {
                const available = isAvailable(d.lastDonated);
                const isMe = currentUser && d.userId === currentUser.uid;
                const isPhoneVisible = visiblePhones[d.id] || isMe;

                return (
                  <motion.div key={d.id} variants={itemVariants} layout className={`bg-white p-5 sm:p-6 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_15px_40px_rgb(220,38,38,0.08)] transition-all duration-300 relative overflow-hidden group flex flex-col justify-between ${isMe ? "border-2 border-red-400" : "border border-slate-100"}`}>
                    {isMe && <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[9px] sm:text-[10px] font-black px-4 py-1 rounded-b-xl tracking-widest shadow-sm z-20">YOUR PROFILE</div>}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-red-50/60 rounded-bl-[80px] -z-10 group-hover:scale-110 transition-transform duration-500"></div>

                    <div className="flex justify-between items-start mb-5 relative z-10">
                      <div className="pr-4">
                        <h3 className="text-xl sm:text-2xl font-black text-slate-800 leading-tight tracking-tight">{d.name}</h3>
                        <div className="flex items-center gap-2 mt-2 text-slate-500">
                          <div className="bg-rose-50 p-1.5 rounded-lg flex-shrink-0"><MapPin size={16} className="text-rose-500" strokeWidth={2.5} /></div>
                          <span className="text-xs sm:text-sm font-bold tracking-wide">{d.place}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0 mt-1">
                        <span className="bg-gradient-to-br from-red-500 to-rose-600 text-white px-3 sm:px-4 py-1.5 rounded-xl text-sm sm:text-base font-black shadow-md shadow-red-500/20">{d.blood}</span>
                        
                        {available ? (
                          <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-black text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-lg">Available</span>
                        ) : (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-black text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg flex items-center gap-1 group/info relative cursor-help">
                              Gap Required <Info size={10} />
                              {/* Floating Tooltip */}
                              <span className="absolute bottom-full right-0 mb-2 w-40 p-2 bg-slate-800 text-white text-[9px] normal-case rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity pointer-events-none z-30 shadow-xl">
                                അവസാനമായി രക്തം നൽകിയിട്ട് 90 ദിവസം പൂർത്തിയായിട്ടില്ല.
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between relative z-10">
                      <div className="flex items-center gap-2.5">
                        <div className="bg-blue-50 p-2 rounded-xl flex-shrink-0"><Phone size={16} className="text-blue-500" strokeWidth={2.5} /></div>
                        <span className="text-sm sm:text-base font-black text-slate-800 tracking-wider">
                          {isPhoneVisible ? d.phone : "••••••••••"}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        {/* 🔥 CALL BUTTON 🔥 */}
                        {isPhoneVisible && !isMe && (
                          <motion.a whileTap={{ scale: 0.9 }} href={`tel:${d.phone}`} className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl transition-all shadow-sm flex items-center justify-center">
                            <PhoneCall size={18} strokeWidth={2.5} />
                          </motion.a>
                        )}

                        {!isMe && (
                          <button onClick={() => togglePhone(d.id)} className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-sm ${isPhoneVisible ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"}`}>
                            {isPhoneVisible ? "Hide" : "Show"}
                          </button>
                        )}

                        {isMe && (
                          <div className="flex gap-2">
                            <button onClick={handleOpenRegister} className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-xl transition-all shadow-sm"><Edit3 size={18} strokeWidth={2.5} /></button>
                            <button onClick={() => handleDeleteClick(d.id)} className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-xl transition-all shadow-sm"><Trash2 size={18} strokeWidth={2.5} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16 bg-white rounded-[2.5rem] border border-dashed border-slate-200 mt-4 max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center p-4 bg-slate-50 rounded-2xl mb-4"><HeartPulse size={32} className="text-slate-300" strokeWidth={2.5} /></div>
              <h3 className="text-lg sm:text-xl font-black text-slate-700 tracking-tight">No Donors Found</h3>
              <p className="text-slate-400 mt-1 text-sm font-medium">നിങ്ങൾ തിരഞ്ഞ വിവരങ്ങളുള്ള ആരുമില്ല.</p>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && currentUser && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-4">
            <div className="absolute inset-0" onClick={() => setIsModalOpen(false)}></div>
            <motion.div initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "100%", opacity: 0 }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className="w-full max-w-lg bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl relative z-10 max-h-[90vh] overflow-y-auto flex flex-col">
              <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-5 flex items-center justify-between rounded-t-[2rem] z-20">
                <h3 className="text-lg sm:text-xl font-black text-slate-800 flex items-center gap-2 tracking-tight"><HeartPulse className="text-red-600" size={22} strokeWidth={2.5}/> {myProfile ? "Edit Your Profile" : "Register as Donor"}</h3>
                <button onClick={() => setIsModalOpen(false)} className="p-2 bg-slate-100 hover:bg-red-50 hover:text-red-500 text-slate-500 rounded-full transition-colors"><X size={18} strokeWidth={2.5} /></button>
              </div>
              <div className="p-6 space-y-5 overflow-y-auto">
                <div>
                  <label className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Full Name</label>
                  <input value={form.name} placeholder="നിങ്ങളുടെ പേര്" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-400 transition text-sm sm:text-base font-semibold text-slate-800 placeholder:font-medium placeholder:text-slate-400" onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Place</label>
                    <input value={form.place} placeholder="സ്ഥലം" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-400 transition text-sm sm:text-base font-semibold text-slate-800 placeholder:font-medium placeholder:text-slate-400" onChange={(e) => setForm({ ...form, place: e.target.value })} />
                  </div>
                  <div className="relative">
                    <label className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Blood Group</label>
                    <select value={form.blood} className="w-full p-4 pr-10 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-400 transition appearance-none text-sm sm:text-base font-bold text-slate-800 cursor-pointer" onChange={(e) => setForm({ ...form, blood: e.target.value })}>
                      <option value="">Select Group</option><option>A+</option><option>A-</option><option>B+</option><option>B-</option><option>O+</option><option>O-</option><option>AB+</option><option>AB-</option>
                    </select>
                    <ChevronDown size={18} strokeWidth={2.5} className="absolute right-4 top-[2.4rem] text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Mobile Number</label>
                  <input type="tel" value={form.phone} placeholder="10 അക്ക നമ്പർ" maxLength={10} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-400 transition text-base tracking-widest font-black text-slate-800 placeholder:font-medium placeholder:tracking-normal placeholder:text-slate-400" onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                <div>
                  <label className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Last Donated (Optional)</label>
                  <div className="relative">
                    <DatePicker selected={form.lastDonated} onChange={(date) => setForm({ ...form, lastDonated: date })} maxDate={new Date()} placeholderText="അവസാനം രക്തം നൽകിയ തിയ്യതി" dateFormat="dd MMM yyyy" className="w-full p-4 pr-10 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-400 transition text-sm sm:text-base font-bold text-slate-800 cursor-pointer placeholder:font-medium placeholder:text-slate-400" />
                    <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" size={20} strokeWidth={2.5} />
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 bg-white sm:rounded-b-[2rem] pb-24 sm:pb-6">
                <button onClick={handlePreSubmit} disabled={loading} className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white p-4 rounded-2xl font-black tracking-wide shadow-lg shadow-red-600/20 transition-all active:scale-95 text-sm sm:text-base flex justify-center items-center gap-2">
                  {loading ? "Processing..." : (myProfile ? "Update Profile" : "Save Details")}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmModal isOpen={confirmConfig.isOpen} message={confirmConfig.message} onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })} onConfirm={executeConfirmAction} />
    </>
  );
}